import { lazy, useMemo, useEffect } from 'react';
import { Route } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ProtectedRoute from '@components/shared/ProtectedRoute';
import AdminLayout from '@components/admin/layouts/AdminLayout';
import { generateAdminRoutes } from '@utils/shared/route-generator';
import { setupDebug } from '@utils/shared/debug-routes';
import { updatePaths } from '@utils/shared/routing/pathManager';
import { renderAdminRoutes } from './shared';
import { useAuth } from '@context/shared/AuthContext';

// Lazy load admin pages
const AdminDashboard = lazy(() => import('@pages/admin/dashboard/page'));
const DebugRoutes = lazy(() => import('@pages/admin/debug-routes'));
const HelpPage = lazy(() => import('@pages/admin/help/page'));
const HelpLayout = lazy(() => import('@pages/admin/help/(help-group)/layout'));
const HelpDetailPage = lazy(() => import('@pages/admin/help/(help-group)/[id]/page'));

/**
 * Get admin routes - prefixed with /admin
 * Routes are dynamically generated from user features/permissions
 * 
 * Note: This is a hook that returns JSX elements, not a component
 * 
 * @param {Array} features - Features from AuthContext
 * @returns {JSX.Element} Admin route configuration
 */
export const useAdminRoutes = (features) => {
  const { featuresLoading, loading } = useAuth();
  
  // Generate admin routes from features (will update when features load)
  const adminRoutes = useMemo(() => {
    // Ensure features is always an array
    const featureArray = Array.isArray(features) ? features : [];
    
    if (featureArray.length === 0) {
      return [];
    }
    
    const routes = generateAdminRoutes(featureArray);
    return routes;
  }, [features]);

  // Setup debug helper in browser console
  useEffect(() => {
    if (features && adminRoutes.length > 0) {
      setupDebug(features, adminRoutes);
      
      // IMPORTANT: Update paths for navigation
      // This was missing from the modular refactor!
      updatePaths(features);
    }
  }, [features, adminRoutes]);
  
  // Determine if we should show loading state
  const isLoadingRoutes = loading || (featuresLoading && adminRoutes.length === 0);

  return (
    <Route
      path="/admin"
      element={
        <ProtectedRoute requireAuth={true} requireAdmin={true}>
          <AdminLayout />
        </ProtectedRoute>
      }
    >
      <Route index element={<AdminDashboard />} />
      <Route path="debug-routes" element={<DebugRoutes />} />
      
      {/* Help routes - static routes not tied to features */}
      <Route path="help">
        <Route index element={<HelpPage />} />
        <Route element={<HelpLayout />}>
          <Route path=":id" element={<HelpDetailPage />} />
        </Route>
      </Route>
      
      {/* Show loading while features are being fetched */}
      {isLoadingRoutes && (
        <Route 
          path="*" 
          element={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '80vh',
              }}
            >
              <CircularProgress />
            </Box>
          } 
        />
      )}
      {!isLoadingRoutes && adminRoutes.length > 0 && renderAdminRoutes(adminRoutes)}
    </Route>
  );
};
